import type { Components } from 'react-markdown';
import {
  renderContentScannerAnchor,
  renderContentScannerInlineCode,
} from './content-scanner-dispatch';
import { ChatCodeBlockShell, CodeText, chatCodeText } from './code-blocks';
import { forwardedHtmlProps, listStyleClass } from './html-attributes';
import { isBlockCode, wrapsCodeElement } from './is-block-code';
import { MermaidBlock } from '../MermaidBlock';
import { CHAT_SIZE_CLASSES, type ChatSize } from './sizes';

// Call this through `buildComponents` in `component-maps.ts`, never directly:
// the returned map's identity is load-bearing for react-markdown.
//
// Every override spreads `forwardedHtmlProps` so the attributes the sanitizer
// allowed (`align`, `colspan`, `id`, `title`, narrowed `style`) survive the
// swap to a styled element instead of being dropped here.
export function createChatComponents(size: ChatSize): Components {
  const s = CHAT_SIZE_CLASSES[size];
  return {
    h1: (props) => (
      <h1
        {...forwardedHtmlProps(props)}
        className={`${s.spacing.heading} ${s.h1} font-semibold text-foreground first:mt-0`}
      >
        {props.children}
      </h1>
    ),
    h2: (props) => (
      <h2
        {...forwardedHtmlProps(props)}
        className={`${s.spacing.heading} ${s.h2} font-semibold text-foreground first:mt-0`}
      >
        {props.children}
      </h2>
    ),
    h3: (props) => (
      <h3
        {...forwardedHtmlProps(props)}
        className={`mt-3 mb-1.5 ${s.h3} font-semibold text-foreground first:mt-0`}
      >
        {props.children}
      </h3>
    ),
    h4: (props) => (
      <h4
        {...forwardedHtmlProps(props)}
        className={`mt-2 mb-1 ${s.h4} font-medium text-foreground first:mt-0`}
      >
        {props.children}
      </h4>
    ),
    p: (props) => (
      <p
        {...forwardedHtmlProps(props)}
        className={`${s.spacing.body} ${s.body} leading-relaxed wrap-anywhere text-foreground/90 last:mb-0`}
      >
        {props.children}
      </p>
    ),
    ul: (props) => (
      <ul
        {...forwardedHtmlProps(props)}
        className={`${s.spacing.body} ml-4 ${listStyleClass(props, 'list-disc')} space-y-0.5 ${s.body} text-foreground/90 last:mb-0`}
      >
        {props.children}
      </ul>
    ),
    ol: (props) => (
      <ol
        {...forwardedHtmlProps(props)}
        className={`${s.spacing.body} ml-4 ${listStyleClass(props, 'list-decimal')} space-y-0.5 ${s.body} text-foreground/90 last:mb-0`}
      >
        {props.children}
      </ol>
    ),
    li: (props) => (
      <li {...forwardedHtmlProps(props)} className="leading-relaxed">
        {props.children}
      </li>
    ),
    strong: (props) => (
      <strong {...forwardedHtmlProps(props)} className="font-semibold text-foreground">
        {props.children}
      </strong>
    ),
    em: (props) => (
      <em {...forwardedHtmlProps(props)} className="italic text-foreground/80">
        {props.children}
      </em>
    ),
    a: ({ children, href }) =>
      renderContentScannerAnchor(href, children, () => (
        <a
          href={href}
          className="text-status-info underline decoration-status-info/30 hover:decoration-status-info/60"
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      )),
    blockquote: (props) => (
      <blockquote
        {...forwardedHtmlProps(props)}
        className={`${s.spacing.body} border-l-2 border-border pl-3 ${s.body} italic text-muted-foreground last:mb-0`}
      >
        {props.children}
      </blockquote>
    ),
    hr: (props) => <hr {...forwardedHtmlProps(props)} className="my-3 border-border/60" />,
    table: (props) => (
      <div
        className={`${s.spacing.body} overflow-x-auto rounded-lg border border-border/50 bg-muted/30 last:mb-0`}
      >
        <table {...forwardedHtmlProps(props)} className={`min-w-full ${s.table} border-collapse`}>
          {props.children}
        </table>
      </div>
    ),
    caption: (props) => (
      <caption
        {...forwardedHtmlProps(props)}
        className={`${s.container.tablePad} text-left text-xs text-muted-foreground`}
      >
        {props.children}
      </caption>
    ),
    thead: (props) => (
      <thead {...forwardedHtmlProps(props)} className="border-b border-border/60 bg-muted/40">
        {props.children}
      </thead>
    ),
    tbody: (props) => (
      <tbody {...forwardedHtmlProps(props)} className="[&>tr:nth-child(even)]:bg-muted/20">
        {props.children}
      </tbody>
    ),
    th: (props) => (
      <th
        {...forwardedHtmlProps(props)}
        className={`${s.container.tablePad} text-left text-xs font-medium text-muted-foreground`}
      >
        {props.children}
      </th>
    ),
    td: (props) => (
      <td
        {...forwardedHtmlProps(props)}
        className={`${s.container.tablePad} ${s.td} border-t border-border/30`}
      >
        {props.children}
      </td>
    ),
    code: ({ children, className }) => {
      const text = String(children).replace(/\n$/, '');
      if (isBlockCode(className, text)) {
        const lang = className?.replace('language-', '') ?? '';
        if (lang === 'mermaid') {
          return <MermaidBlock code={text} size={size} />;
        }
        return (
          <ChatCodeBlockShell size={size} lang={lang} code={text}>
            <CodeText code={text} className={chatCodeText(size)} />
          </ChatCodeBlockShell>
        );
      }
      return renderContentScannerInlineCode(text, (content) => (
        <code
          className={`rounded-sm bg-muted/60 px-1.5 py-0.5 ${s.inlineCode} font-mono wrap-anywhere text-foreground/90`}
        >
          {content}
        </code>
      ));
    },
    pre: (props) =>
      wrapsCodeElement(props.node) ? (
        <>{props.children}</>
      ) : (
        <pre
          {...forwardedHtmlProps(props)}
          className={`${s.spacing.body} overflow-x-auto rounded-lg border border-border/50 bg-muted/30 p-3 font-mono ${s.inlineCode} leading-relaxed last:mb-0`}
        >
          {props.children}
        </pre>
      ),
  };
}
